<script setup lang="ts">
/* =============================================================
   STAT LINK
   =============================================================
   A dashboard figure that is also the way to the screen behind
   it. Deliberately a <RouterLink> rather than a tile with a
   click handler: it is a real link, so it gets middle-click,
   ⌘-click, "open in new tab", a status-bar preview and keyboard
   focus for free, and screen readers announce it as a link
   rather than as text that happens to react.

   The app's StatTile is the read-only sibling of this. Kept
   separate rather than given a `to` prop, because a tile that
   is sometimes navigable and sometimes not is the kind of
   ambiguity that ends up styled two ways.
   ============================================================= */

import { RouterLink } from 'vue-router'
import Icon from '@/components/Icon.vue'
import type { IconName } from '@/components/icons'

withDefaults(
  defineProps<{
    label: string
    /** Already formatted — the call site decides on separators. */
    value: string
    to: string
    icon?: IconName
    /** Small line under the value, e.g. "Across all companies". */
    note?: string
    /** Shows a placeholder bar instead of the value. */
    loading?: boolean
  }>(),
  { loading: false },
)
</script>

<template>
  <RouterLink :to="to" class="tile">
    <div class="top">
      <span class="label">{{ label }}</span>
      <span v-if="icon" class="icon">
        <Icon :name="icon" :size="16" />
      </span>
    </div>

    <span v-if="loading" class="skeleton" aria-hidden="true" />
    <p v-else class="value">{{ value }}</p>

    <p class="note">
      <span v-if="note">{{ note }}</span>
      <span class="go">
        Open
        <Icon name="arrowRight" :size="14" />
      </span>
    </p>
  </RouterLink>
</template>

<style scoped>
.tile {
  display: flex;
  flex-direction: column;
  gap: var(--space-3xs);
  padding: var(--space-sm) var(--space-md);
  color: inherit;
  text-decoration: none;
  background-color: var(--color-surface);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  transition:
    border-color var(--dur-fast) var(--ease),
    box-shadow var(--dur-fast) var(--ease),
    transform var(--dur-fast) var(--ease);
}

.tile:hover {
  border-color: var(--color-accent);
  box-shadow: var(--shadow-md);
  transform: translateY(calc(-1 * var(--lift)));
}

.tile:active {
  transform: translateY(0);
}

@media (prefers-reduced-motion: reduce) {
  .tile,
  .tile:hover {
    transform: none;
  }
}

.top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2xs);
}

.label {
  font-size: var(--text-xs);
  font-weight: var(--weight-medium);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-caps);
}

.icon {
  display: grid;
  place-items: center;
  width: 1.75rem;
  height: 1.75rem;
  color: var(--color-accent);
  background-color: var(--color-accent-soft);
  border-radius: var(--radius-sm);
  transition: background-color var(--dur-fast) var(--ease);
}

.tile:hover .icon {
  color: var(--color-accent-contrast);
  background-color: var(--color-accent);
}

.value {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: var(--weight-semibold);
  line-height: var(--leading-tight);
  letter-spacing: var(--tracking-tight);
  font-variant-numeric: tabular-nums;
  color: var(--color-heading);
}

.note {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2xs);
  margin-top: var(--space-3xs);
  font-size: var(--text-xs);
  color: var(--color-text-faint);
}

.go {
  display: inline-flex;
  align-items: center;
  gap: var(--space-3xs);
  margin-inline-start: auto;
  font-weight: var(--weight-semibold);
  color: var(--color-accent);
}

.go svg {
  transition: transform var(--dur-fast) var(--ease);
}

.tile:hover .go svg {
  transform: translateX(2px);
}

.skeleton {
  display: block;
  width: 3ch;
  height: var(--text-2xl);
  background-color: var(--color-surface-sunken);
  border-radius: var(--radius-xs);
  animation: shimmer 1.2s var(--ease) infinite;
}

@keyframes shimmer {
  50% {
    opacity: 0.45;
  }
}

@media (prefers-reduced-motion: reduce) {
  .skeleton {
    animation: none;
  }
}
</style>
